// Module B (triple multimodal fusion judgment) · Module C (HITL autonomous quality control)
const moduleb = {
  nav: {
    brandTitle: 'Module B · Triple Multimodal Fusion Defect Judgment',
    brandSub: 'Process · Vision · Thermal',
    steps: {
      sync: { title: 'Shot-Product Sync', desc: 'Shot-Product Sync' },
      features: { title: 'Feature Extraction & Matching', desc: 'Feature Extraction' },
      fusion: { title: 'Triple Weighted Fusion', desc: 'Triple Fusion' },
      judgment: { title: 'Overall Defect Judgment', desc: 'Defect Judgment' },
    },
  },
  common: {
    selectProduct: 'Select Product',
    resultOk: 'OK',
    resultNg: 'NG',
    judgeOk: 'Normal',
    shotId: 'Shot ID',
    productId: 'Product ID',
    time: 'Time',
    unitCount: '',
    newBadge: 'NEW',
    tempSensitive: 'Temp-sensitive',
    sources: {
      process: 'Process',
      vision: 'Vision',
      thermal: 'Thermal',
      temperature: 'Temperature',
    },
  },
  defects: {
    warpage: 'Warpage',
    shrinkage: 'Shrinkage',
    dimension: 'Dimension',
    surface: 'Surface Defect',
    shortShot: 'Short Shot',
    flash: 'Flash',
  },
  sync: {
    subtitle: 'Determines whether process, vision and thermal data belong to the same product (1 shot = 1 product) via the Shot-Product Sync engine',
    meta: 'Line: Injection #1 · CAV-A · Real-time',
    timelineTitle: 'Same-Product Matching Timeline',
    timelineHint: 'Dashed vertical line = three sources matched to the same product',
    tableTitle: 'Recent Shot-Product Matching',
    confidence: 'Match Confidence',
    judgement: 'Result',
    detailTitle: 'Matching Detail',
    refLabel: 'Matched Product · Reference Time',
    sourceKeyLabel: 'Source Key / Match Confidence',
    detailNote: 'Identity keys and timestamps of the three sources are compared to decide whether they belong to the same product. If any source fails to match, the shot is excluded from fusion.',
    drilldown: 'Analyze fusion for {product}',
    drilldownBlocked: 'Same-product matching failed · Fusion unavailable',
    matched: 'matched',
    unmatched: 'Unmatched',
    missing: '(missing)',
    stats: {
      totalShots: 'Total Shots',
      matchRate: 'Same-Product Match Rate',
      confidence: 'Avg. Match Confidence',
      mismatch: 'Mismatches',
    },
    status: {
      match: 'Match',
      check: 'Check',
      mismatch: 'Mismatch',
    },
  },
  features: {
    subtitle: 'Extracts features of three modalities (time series · shape · thermal field) from one synchronized shot and registers them to a common product coordinate system',
    meta: 'Target Shot',
    selectShot: 'Select Shot',
    panelProcess: 'Time Series · Process (Sₚ)',
    panelVision: 'Shape · Vision (Sᵥ)',
    panelThermal: 'Thermal Field · Thermal (Sₜ)',
    imagePlaceholder: 'Product Image',
    extracted: 'Extracted Features',
    registrationTitle: 'Registration — aligning vision ROI and thermal coordinates onto a common product grid',
    visionRoi: 'Vision ROI',
    thermalHotspot: 'Thermal Hotspot',
    regAccuracy: 'Registration Accuracy',
    offsetX: 'Offset (X)',
    offsetY: 'Offset (Y)',
    legendVision: 'Vision shape coordinates',
    legendThermal: 'Thermal field coordinates',
    legend: {
      pressure: 'Inj. Pressure (MPa)',
      velocity: 'Inj. Speed (mm/s)',
      holding: 'Holding (MPa)',
    },
    process: {
      peakPressure: 'Peak Inj. Pressure',
      holdTime: 'Holding Time',
      fillTime: 'Fill Time',
      chargeTime: 'Charge Time',
      maxSpeed: 'Max Inj. Speed',
      cushion: 'Cushion',
    },
    vision: {
      edgeDensity: 'Edge Density',
      defectCandidates: 'Defect Candidate Regions',
      contourCompleteness: 'Contour Completeness',
      roiAreaRatio: 'ROI Area Ratio',
      roiShape: 'ROI-Shape',
      roiDefect: 'Defect Candidate',
    },
    thermal: {
      maxTemp: 'Max Temperature',
      avgTemp: 'Avg Temperature',
      hotspot: 'Hotspot Coordinates',
      stdDev: 'Temp. Deviation (σ)',
    },
  },
  fusion: {
    subtitle: 'Vision, process and temperature each judge OK/NG, then are fused with predefined fixed weights to produce the final fusion score S (modular robust fusion)',
    formulaNote: 'The γ term (thermal) is newly added to directly judge temperature-sensitive defects',
    modalityTitle: 'Per-Modality OK/NG Judgment · Fixed Weights',
    fixedWeight: 'Fixed Weight',
    weightSum: 'Weights are predefined fixed values (α 35% · β 35% · γ 30%)',
    weightSumNote: '— renormalized automatically only when a modality is missing',
    contribTitle: 'Contribution (weight × score)',
    donutLabel: 'Fusion S',
    resultTitle: 'Final Fusion Score S',
    thresholdLabel: 'Judgment Threshold',
    verdictNg: 'S ≥ threshold → NG candidate',
    verdictOk: 'S < threshold → OK candidate',
    next: 'Go to overall judgment for {product}',
    degradeTitle: 'Graceful Degradation — automatic switch when a modality is missing',
    activeCount: '{n}-modal fusion',
    stateActive: 'Active',
    stateMissing: 'Missing',
    degradeBadge: 'Thermal missing → auto switch to 2-modal fusion (γ=0, α·β renormalized)',
    degradationNote: 'When thermal data is missing, γ is set to 0 and α·β are renormalized to sum to 1, automatically switching to 2-modal fusion.',
  },
  judgment: {
    subtitle: 'Uses the triple fusion result (S) from STEP 3 to determine the final OK/NG and defect type, and presents the contributing modality as evidence',
    fusionScore: 'Fusion Score S',
    confidence: 'Confidence',
    threshold: 'Threshold {value}',
    thresholdOver: 'exceeded → NG',
    thresholdUnder: 'below → OK',
    defectTypeTitle: 'Defect Type Classification',
    legendOver: 'Types over threshold',
    contribTitle: 'Judgment Evidence · Contributing Modality',
    tempLed: 'Temp-sensitive lead',
    noteHead: 'This judgment is driven by {label} at {percent}% contribution,',
    noteThermal: 'thermal imaging led the detection of temperature-sensitive defects (warpage, shrinkage).',
    noteOther: 'the {label} signal led the judgment.',
    annotationTitle: 'Defect Region Annotation',
    productImage: 'Product Image',
    annotationNg: 'Suspected defect regions highlighted',
    annotationOk: 'No anomalous region (OK)',
    historyTitle: 'Recent Judgment History',
    verdict: 'Verdict',
    defectType: 'Defect Type',
    contribModality: 'Contributing Modality',
    kpi: {
      f1: 'Overall F1',
      recall: 'Temp-sensitive Recall',
      total: 'Cumulative Judgments',
      rate: 'Defect Rate',
    },
    badge: {
      ok: 'Good (OK)',
      ng: 'Defect (NG)',
    },
    contrib: {
      thermal: 'Thermal (Sₜ)',
      vision: 'Vision (Sᵥ)',
      process: 'Process (Sₚ)',
    },
    annotations: {
      warpage: 'Suspected warpage',
      shrinkage: 'Suspected shrinkage',
    },
  },
};

const modulec = {
  nav: {
    brandTitle: 'Module C · HITL Autonomous Quality Control with Temperature',
    brandSub: 'AI recommendation → operator approval → triple safeguard (threshold block · rollback · in-silico)',
    steps: {
      recommend: { title: 'Recommendation & Approval', desc: 'Recommend & Approve' },
      safeguard: { title: 'Safeguard', desc: 'Safeguard' },
    },
  },
  common: {
    countSuffix: '{n}',
    tempTag: 'Temp',
  },
  params: {
    injectionPressure: 'Injection Pressure',
    injectionSpeed: 'Injection Speed',
    holdingPressure: 'Holding Pressure',
    moldTemp: 'Mold Temperature',
    coolingTime: 'Cooling Time',
  },
  paramStatus: {
    normal: 'Normal',
    warn: 'Warning',
    exceed: 'Over Threshold',
  },
  recCaseStatus: {
    pending: 'Pending',
    approved: 'Approved',
  },
  approvalStatus: {
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
  },
  overallStatus: {
    safe: 'Safe (Normal)',
    blocked: 'Blocked',
    rollbackWait: 'Rollback Pending',
  },
  changeLogStatus: {
    applied: 'Applied',
    rolledback: 'Rolled back',
    blocked: 'Blocked',
  },
  worker: {
    name: 'Kim Hyunsoo',
    name2: 'Lee Jungmin',
    shift: 'Day Shift A',
    role: 'Process Engineer',
    aiAuto: 'AI Auto',
  },
  timeline: {
    requested: { label: 'Approval Requested', desc: 'AI recommendation generated · approval requested' },
    reviewing: { label: 'Operator Reviewing', desc: 'Kim Hyunsoo (Day Shift A) reviewing' },
    decision: { label: 'Approve / Reject', desc: 'Awaiting operator decision' },
    safeguard: { label: 'Safeguard Verification', desc: 'Forwarded to safeguard stage upon approval' },
  },
  modal: {
    changeSummary: 'Change Summary ({n} parameters)',
    param: 'Parameter',
    current: 'Current',
    recommended: 'Recommended',
    worker: 'Operator',
    confirmApply: 'Confirm & Apply',
    cancel: 'Cancel',
  },
  recommend: {
    title: 'Recommendation Generation',
    subtitle: 'AI automatically recommends optimal process conditions based on Module B fusion judgment results (defect and cause)',
    meta: 'Target Shot',
    listTitle: 'Recommendations',
    paramTitle: 'Recommended Conditions · Current → Recommended',
    expectedReduction: 'Expected Defect Reduction',
    confidence: 'Recommendation Confidence',
    approvedBanner: 'Recommendation approved — forwarded to safeguard verification',
    approvedShort: 'Recommendation approved',
    goSafeguard: 'Go to Safeguard',
    approveHint: 'Review the selected recommendation and approve it. Upon approval it is applied on site after a confirmation step.',
    approveBtn: 'Approve Recommendation',
    modalTitle: 'Confirm Recommendation Apply',
    modalMessage: 'Apply the recommended conditions for {product} ({shot}) on site?',
    modalWarning: 'Changes take effect on the process immediately. They can be rolled back later in the safeguard stage.',
    cols: {
      param: 'Process Parameter',
      current: 'Current',
      recommended: 'Recommended',
      delta: 'Delta (Δ)',
      range: 'Threshold Range',
      status: 'Status',
    },
    notes: {
      rec2048: 'Raising mold temperature and shortening cooling time is expected to directly reduce temperature-sensitive defects (warpage, shrinkage).',
      rec2043: 'Raising injection and holding pressure is expected to stabilize dimensional defects (process-driven).',
      rec2041: 'Raising mold temperature leads the reduction of temperature-sensitive defects (warpage). Note the proximity to the threshold boundary.',
    },
  },
  safeguard: {
    subtitle: 'Triple safeguard securing safety before and after applying approved conditions on site (threshold block · rollback · in-silico pre-validation)',
    guard1: '① Automatic Threshold Block',
    guard2: '② Rollback · Change Log',
    guard3: '③ In-silico Pre-validation',
    blocked: 'Blocked',
    normal: 'Normal',
    guard1NoteBlocked: 'A recommended value outside the threshold range was detected, so the apply was automatically blocked.',
    guard1NoteOk: 'All recommended values are within the threshold range and can be applied.',
    rollbackBtn: 'Roll back to previous',
    rollbackRequested: 'Rollback requested',
    passed: 'Passed',
    failed: 'Failed',
    beforeExpected: 'Expected Before',
    afterExpected: 'Expected (Simulated)',
    twinVerified: 'Digital twin simulation verified',
    silicoNote: 'Fully automatic control is performed only up to digital twin virtual validation; direct application on site always requires operator approval.',
    verifyTitle: 'Defect Rate Trend Before/After Apply',
    defectRate: 'Defect Rate',
    applied: 'Applied',
    reduction: 'Defect Rate Reduction',
    significance: 'Statistical Significance',
    cumulativeShots: 'Cumulative Shots Applied',
    hourLabel: '{h}:00',
    rollbackTitle: 'Roll back to previous conditions',
    rollbackMessage: 'Cancel the currently applied conditions and restore the previous process conditions?',
    rollbackWarning: 'On rollback the current recommendation is released immediately and values return to the previous state.',
    rollbackConfirm: 'Execute Rollback',
    cols: {
      time: 'Time',
      param: 'Parameter',
      change: 'Change',
      actor: 'Applied By',
      status: 'Status',
    },
  },
};

const moduleI18n = { moduleb, modulec };

export default moduleI18n;
